import { runConvexQuery } from '@/lib/convex';

// Slice 3 (2026-04-27 acquisition reset) frontend service for the OS Product Library.
// Wraps convex/productLibrary.ts:getForUser behind a small typed surface the OS
// ProductLibrary page can call. Codebase convention: HTTP Convex client + Clerk token via
// useAppAuth().getToken(); no convex/react hooks.

export type ProductOwnership = 'owned' | 'not_owned';

export interface ProductLibraryProductCard {
  productId: string;
  name: string;
  ownership: ProductOwnership;
  purchasedAt: string | null;
  description: string;
  notOwnedCtaLabel: string | null;
}

export interface ProductLibraryBumpCard {
  productId: string;
  priceId: string | null;
  purchasedAt: string;
  name: string;
}

export type ProductLibrarySubscriptionStateName =
  | 'active'
  | 'grace'
  | 'inactive'
  | 'cancelling'
  | 'past_due'
  | 'never_started';

export interface ProductLibrarySubscriptionState {
  state: ProductLibrarySubscriptionStateName;
  graceDaysRemaining: number | null;
  currentPeriodEndAt: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface ProductLibraryBlueprintCard {
  blueprintId: string;
  status: 'draft' | 'accepted' | 'superseded';
  recommendedWrapper: string;
  acceptedAt: string | null;
  createdAt: string;
}

export interface ProductLibraryXrayCard {
  status: 'not_started' | 'in_progress' | 'report_draft' | 'completed';
  reportStatus: 'draft' | 'accepted' | 'legacy_completed' | null;
}

export interface ProductLibraryBuildRoomCard {
  sessionId: string;
  status: 'intake' | 'build_brief' | 'building' | 'needs_decision' | 'qa_review' | 'ready' | 'handed_off' | 'blocked';
  sourceBlueprintId: string | null;
  openDecisionCount: number;
  artifactCount: number;
  qaVerdict: 'pass' | 'hold' | 'fail' | null;
  handoffAt: string | null;
  updatedAt: string;
}

export interface ProductLibrarySnapshot {
  userId: string;
  computedAt: string;
  products: {
    kickstart: ProductLibraryProductCard;
    brandLabOs: ProductLibraryProductCard;
    academy: ProductLibraryProductCard;
    caseStudy: ProductLibraryProductCard;
  };
  bumps: ProductLibraryBumpCard[];
  subscription: ProductLibrarySubscriptionState;
  blueprint: ProductLibraryBlueprintCard | null;
  xray: ProductLibraryXrayCard;
  buildRoom: ProductLibraryBuildRoomCard | null;
}

export async function getProductLibraryForUser(token?: string | null) {
  return runConvexQuery<ProductLibrarySnapshot | null>(
    'productLibrary:getForUser',
    {},
    token,
  );
}
